import { useState, useCallback } from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

interface UseCustomAmountReturn {
  customAmount: number | null;
  useCustomAmount: boolean;
  setCustomAmount: (value: number | null) => void;
  setUseCustomAmount: (value: boolean) => void;
  handleCustomAmountChange: (value: number | null) => void;
  handleUseCustomAmountChange: (e: CheckboxChangeEvent) => void;
  getFinalAmount: (defaultAmount: number | null) => number | null;
  reset: () => void;
}

/**
 * Hook for managing custom amount state and logic
 */
export const useCustomAmount = (initialDefaultAmount: number | null = null): UseCustomAmountReturn => {
  const [customAmount, setCustomAmountState] = useState<number | null>(null);
  const [useCustomAmount, setUseCustomAmountState] = useState(false);

  const handleCustomAmountChange = useCallback((value: number | null) => {
    setCustomAmountState(value);
    if (value !== null) {
      setUseCustomAmountState(true);
    }
  }, []);

  const handleUseCustomAmountChange = useCallback((e: CheckboxChangeEvent) => {
    setUseCustomAmountState(e.target.checked);
    if (!e.target.checked) {
      setCustomAmountState(null);
    }
  }, []);

  const getFinalAmount = useCallback((defaultAmountValue: number | null): number | null => {
    if (useCustomAmount && customAmount !== null) {
      return customAmount;
    }
    return defaultAmountValue;
  }, [useCustomAmount, customAmount]);

  const reset = useCallback(() => {
    setCustomAmountState(null);
    setUseCustomAmountState(false);
  }, []);

  return {
    customAmount,
    useCustomAmount,
    setCustomAmount: setCustomAmountState,
    setUseCustomAmount: setUseCustomAmountState,
    handleCustomAmountChange,
    handleUseCustomAmountChange,
    getFinalAmount,
    reset
  };
};

